import { httpClient } from '@shared/infrastructure/http/http-client.js';
import type {
  AnalysisMetadata,
  AnalysisProvenance,
  CandidatesSummary,
  CoherenceNotes,
  CoherenceRuleOrigin,
  CoherenceRuleStatus,
  ContradictionKind,
  EngineVersions,
  EnginesRun,
  GestureAlert,
  GestureAnalysis,
  GestureEpisode,
  GestureExplain,
  LandmarkFrame,
  LandmarkWindow,
  RunConfigRef,
  RunStage,
  RunStatus,
  SemanticAlert,
  SemanticAlertOrigin,
  SemanticBlock,
  SemanticLayer,
  SemanticWord,
  Transcription,
  CoherenceAlert,
  CoherenceConfig,
  CoherenceLayer,
  CoherenceRulesNotes,
  CoherenceCompareWith,
  RuleFinding,
  RuleFindingReason,
  SemanticContradiction,
} from '../model.js';
import { type RulePayload, toRule } from './analysis-configs.api.js';

interface EnginePayload {
  tool_version?: string;
  build_sha?: string;
  engines?: Record<string, string>;
  resolved?: Record<string, unknown>;
  silent_fallbacks?: Array<{ name: string; detail: string; at: string }>;
  degraded?: boolean;
  degraded_reasons?: string[];
}

interface MetadataPayload {
  video_url: string | null;
  video: {
    id: number;
    name: string;
    description: string | null;
    created_at: string | null;
    duration_seconds: number;
    processing_seconds?: number | null;
    analysis_started_at?: string | null;
    analysis_completed_at?: string | null;
    status: string | null;
  };
  analysis_status: string | null;
  analysis_progress: number | null;
  /**
   * Upstream's run block, passed through as it comes.
   *
   * Every field optional: a result older than the ledger has no run at all, and the backfilled
   * ones carry fewer fields than the ledger's own.
   */
  run: {
    run_id?: string | null;
    status?: RunStatus | null;
    attempt?: number;
    is_active?: boolean;
    is_pinned?: boolean;
    partial?: boolean;
    batch?: string | null;
    note?: string | null;
    started_at?: string | null;
    finished_at?: string | null;
    stages?: StagePayload[] | null;
    engines_requested?: string[];
    engines_executed?: string[];
    engines_skipped?: Array<{ name: string; reason: string }>;
    engine?: EnginePayload | null;
    /** At the root of the run, not inside `config`: that is where this file used to look. */
    engines_hash?: string | null;
    config?: {
      /** Upstream's name for it. `interview_analysis_config_id` is kept only as a fallback. */
      id?: number | null;
      interview_analysis_config_id?: number | null;
      gestures_mongo_config_id?: string | null;
      version?: number | null;
      engines_hash?: string | null;
    } | null;
  } | null;
  provenance?: ProvenancePayload | null;
  landmarks_retained: boolean;
  landmarks_config: {
    landmarks_total_frames?: number;
    sampling_fps?: number;
    total_frames?: number;
    fps?: number;
  } | null;
}

/**
 * The gesture analysis, in upstream's own spelling.
 *
 * Declared loosely: these documents are written by the Python engine and grow fields with every
 * release. Narrowing what nothing reads would mean a release here for each of those.
 */
export interface GesturesPayload {
  /**
   * Where the coherence alerts actually are.
   *
   * Not at the root, which is where this file read them until a real payload was looked at: a
   * video with thirty alerts produced an empty list, so the timeline drew gesture marks with no
   * verdict on any of them and the semantic panel had nothing to show. Nothing failed — the
   * optional chain answered `undefined` and the `?? []` turned it into «there are none».
   *
   * Absent, not null, on a run that did not execute coherence: upstream drops the key.
   */
  coherence_analysis?: {
    ruleset_version?: string | null;
    coherence_alerts?: CoherenceAlertPayload[];
    /** The coherence rules that fired (engine 7.0.0). Absent on every document older than them. */
    semantic_alerts?: SemanticAlertPayload[] | null;
    notes?: NotesPayload | null;
    /**
     * The words and blocks the semantic panel is drawn from.
     *
     * `/transcription` has no word timings and ignores the run; these are the run's own, already
     * in milliseconds. Optional in the engine's contract, so a document without them still has
     * its alerts.
     */
    semantic_modes?: {
      diarization?: DiarizationPayload | null;
      punctuation?: DiarizationPayload | null;
    } | null;
  } | null;
  /** The configuration the run sealed. At the root, not inside `coherence_analysis`. */
  analysis_config_snapshot?: {
    interview_analysis_config_id?: number | null;
    version?: number | null;
    engines?: { coherence?: CoherenceSnapshotPayload | null } | null;
  } | null;
  /** The provenance stamp, spread at the root of the document. */
  engine?: EnginePayload | null;
  schema_version?: string | null;
  analysis_config_id?: number | null;
  analysis_config_version?: number | null;
  analysis_config_hash?: string | null;
  engines_hash?: string | null;
  degraded?: boolean | null;
  /** Only on documents written since the engine selection existed. */
  engines_requested?: string[] | null;
  engines_executed?: string[] | null;
  engines_skipped?: Array<{ name?: string; reason?: string }> | null;
  partial?: boolean | null;
  episodes?: Array<{
    episode_id: string;
    gesture_name: string;
    start_time_ms: number;
    end_time_ms: number;
    duration_ms: number;
    classification: string;
    should_show?: boolean;
    analysis_status?: string | null;
    analysis_method?: string | null;
    /**
     * At the top level, not inside `metadata`.
     *
     * It was read from `metadata.discard_reason` here, which is `undefined` on every episode the
     * engine sends — so every discarded gesture explained itself as «sin motivo registrado».
     */
    discard_reason?: string | null;
    frequency_band?: string | null;
    absolute_count?: number | null;
    confidence_summary?: { mean?: number; min?: number; max?: number } | null;
    metadata?: Record<string, unknown> | null;
  }>;
  alerts?: Array<{
    episode_id: string;
    gesture_name: string;
    timestamp_ms: number;
    duration_ms: number;
    reason: string;
    gesture_kind?: string | null;
    is_subject_speaking?: boolean | null;
    explain?: Record<string, unknown> | null;
  }>;
  /** Read as a fallback only: kept in case a future shape hoists them back to the root. */
  coherence_alerts?: CoherenceAlertPayload[];
  baseline?: { per_gesture_frequency_hz?: Record<string, number> } | null;
}

interface CoherenceAlertPayload {
  alert_id: string;
  source_alert_episode_id?: string | null;
  gesture_name: string;
  gesture_timestamp_ms: number;
  gesture_time_range_ms?: number[] | null;
  rule_id?: string | null;
  rule_label?: string | null;
  semantic_match?: {
    text?: string | null;
    start_time_ms?: number | null;
    end_time_ms?: number | null;
    speaker?: string | null;
  } | null;
  semantic_timing_alignment_ms?: number | null;
  matched?: boolean;
  explanation?: string | null;
  coherence_before?: number | null;
  coherence_reduction?: number;
  coherence_after?: number | null;
  rule_reduction_pct?: number;
  reason_category?: string | null;
  confidence?: number | null;
  metadata?: {
    window_seconds?: number | null;
    reduction_source?: string | null;
    source_item?: string | null;
    source_value?: string | null;
    segment_strategy?: string | null;
    semantic_candidate_source?: string | null;
    gesture_kind?: string | null;
    is_subject_speaking?: boolean | null;
    semantic_block_ids_diarization_per_alert?: string[] | null;
    semantic_block_ids_diarization_merged?: string[] | null;
    semantic_block_ids_diarization_merged_parent?: string[] | null;
  } | null;
  explain?: Record<string, unknown> | null;
}

/** As `_semantic_alert_to_doc` writes it. `contradicts` carries different keys for each kind. */
interface SemanticAlertPayload {
  alert_id?: string | null;
  block_id?: string | null;
  rule_id?: string | null;
  rule_label?: string | null;
  origin?: string | null;
  text?: string | null;
  start_time_ms?: number | null;
  end_time_ms?: number | null;
  speaker?: string | null;
  question?: string | null;
  contradicts?: {
    kind?: string | null;
    text?: string | null;
    block_id?: string | null;
    start_time_ms?: number | null;
    end_time_ms?: number | null;
    source_id?: string | number | null;
    source_name?: string | null;
  } | null;
  explanation?: string | null;
  confidence?: number | null;
  coherence_reduction?: number | null;
}

interface WordPayload {
  word_index?: number | null;
  text?: string | null;
  start_time_ms?: number | null;
  end_time_ms?: number | null;
  speaker?: string | null;
  source_segment_index?: number | null;
}

interface BlockPayload {
  block_id?: string | null;
  start_time_ms?: number | null;
  end_time_ms?: number | null;
  text?: string | null;
  speaker?: string | null;
  source_segment_index?: number | null;
  contributing_alert_ids?: Array<string | number> | null;
  highlight_word_indexes?: number[] | null;
  anchor_word_index?: number | null;
  block_kind?: string | null;
  parent_block_id?: string | null;
  child_block_ids?: string[] | null;
  score_resta_computada_after?: number | null;
  score_alert_major_after?: number | null;
  aggregate_coherence_after?: number | null;
  aggregate_alert_count?: number | null;
  aggregate_matched_count?: number | null;
  below_threshold_sum_reduction?: boolean | null;
  below_threshold_alert_major?: boolean | null;
  /** Since engine 7.0.0. */
  semantic_alert_ids?: Array<string | number> | null;
}

interface DiarizationPayload {
  words?: WordPayload[] | null;
  /**
   * The engine's older single list. Read for the merged strategy when `blocks_merged` is missing,
   * and for the ideas, by kind, when `blocks_idea` is.
   */
  blocks?: BlockPayload[] | null;
  blocks_merged?: BlockPayload[] | null;
  blocks_merged_parents?: BlockPayload[] | null;
  blocks_per_alert?: BlockPayload[] | null;
  blocks_idea?: BlockPayload[] | null;
  blocks_none?: BlockPayload[] | null;
  speaker_names?: Record<string, { name?: string | null; confidence?: number | null }> | null;
}

interface NotesPayload {
  alerts_total?: number | null;
  coherence_rows?: number | null;
  alerts_without_rule_binding?: number | null;
  duplicate_signal_rows?: number | null;
  transcription_segments?: number | null;
  bulk_batches?: number | null;
  bulk_items?: number | null;
  segment_strategy?: string | null;
  candidates_summary?: {
    threshold_pct?: number | null;
    idea_blocks_total?: number | null;
    below_threshold_sum_reduction_total?: number | null;
    below_threshold_alert_major_total?: number | null;
    candidates?: Array<{
      block_id?: string | null;
      start_time_ms?: number | null;
      end_time_ms?: number | null;
      text?: string | null;
      score_resta_computada_after?: number | null;
      score_alert_major_after?: number | null;
      gestures?: Array<{
        gesture_name?: string | null;
        rule_id?: string | null;
        coherence_reduction?: number | null;
        frequency_band?: string | null;
        reason?: string | null;
      }> | null;
    }> | null;
  } | null;
  /** Since engine 7.0.0. */
  coherence?: {
    threshold_pct?: number | null;
    person_id?: number | null;
    semantic_alerts?: number | null;
    rules?: Array<{
      rule_id?: string | null;
      label?: string | null;
      origin?: string | null;
      reduction_pct?: number | null;
      status?: string | null;
      alerts?: number | null;
      /** Since engine 7.3.0. */
      min_confidence?: number | null;
      compare_with?: string | null;
      calls?: number | null;
      findings?: number | null;
      discarded?: number | null;
    }> | null;
    /** Since engine 7.3.0: every finding of the LLM, kept or discarded. */
    rule_findings?: RuleFindingPayload[] | null;
  } | null;
}

export interface RuleFindingPayload {
  rule_id?: string | null;
  idea_index?: number | null;
  block_id?: string | null;
  start_time_ms?: number | null;
  end_time_ms?: number | null;
  text?: string | null;
  contradicts?: SemanticAlertPayload['contradicts'];
  explanation?: string | null;
  confidence?: number | null;
  status?: string | null;
  reason?: string | null;
}

interface CoherenceSnapshotPayload {
  semaphoreThresholdGood?: unknown;
  semaphoreThresholdWarn?: unknown;
  ideaCoincidenceThresholdPct?: unknown;
  detectionThresholdPct?: unknown;
  segmentStrategy?: string | null;
  semanticWindowSeconds?: number | null;
  transcriptionProvider?: string | null;
  ruleset?: { version?: string | null; rules?: RulePayload[] | null } | null;
}

interface ProvenancePayload {
  schema_version?: string | null;
  analysis_config_id?: number | null;
  analysis_config_version?: number | null;
  analysis_config_hash?: string | null;
  engines_hash?: string | null;
  degraded?: boolean | null;
  engine?: EnginePayload | null;
}

interface StagePayload {
  name: string;
  status: RunStage['status'];
  started_at?: string | null;
  finished_at?: string | null;
  duration_ms?: number | null;
}

/**
 * The transcription, in upstream's own spelling.
 *
 * One array named after the document, not the `{segments, words}` this file used to expect — so
 * both came back empty and the semantic panel had no text at all.
 *
 * It ignores `?run`: it is the video's latest text, shared by every run. The karaoke reads the
 * run's own diarised words from the gestures document instead; this is only the plain-text
 * fallback for a run whose document has none. A video's own transcription, once done, also brings
 * its `words` — the bundle coherence reads, `word` in seconds and no speaker (2026-09-28); the
 * legacy text has none.
 */
interface TranscriptionPayload {
  transcription?: Array<{
    startTime: number;
    endTime: number;
    speaker?: string | null;
    text: string;
  }>;
  segments?: Array<{ startTime: number; endTime: number; speaker?: string; text: string }>;
  words?: Array<{ word: string; start_time: number; end_time: number }>;
}

interface LandmarksPayload {
  frames?: Array<{
    timestamp_ms?: number;
    face?: Array<{ x: number; y: number }>;
    left_hand?: Array<{ x: number; y: number }>;
    right_hand?: Array<{ x: number; y: number }>;
  }>;
  start_seconds: number;
  end_seconds: number;
}

function date(value: string | null | undefined): Date | null {
  return value ? new Date(value) : null;
}

/** Seconds on the wire, milliseconds everywhere here: one unit, and it is the engine's. */
function toMs(seconds: number): number {
  return Math.round(seconds * 1_000);
}

function toEngine(payload: EnginePayload | null | undefined): EngineVersions | null {
  if (!payload) return null;

  return {
    toolVersion: payload.tool_version ?? '',
    buildSha: payload.build_sha ?? '',
    engines: payload.engines ?? {},
    resolved: payload.resolved ?? {},
    silentFallbacks: (payload.silent_fallbacks ?? []).map((fallback) => ({
      name: fallback.name,
      detail: fallback.detail,
      at: new Date(fallback.at),
    })),
    degraded: payload.degraded === true,
    degradedReasons: payload.degraded_reasons ?? [],
  };
}

/**
 * The configuration a run names.
 *
 * Upstream sends `config.id` and puts the hash at the root of the run. This read
 * `config.interview_analysis_config_id` and `config.engines_hash` instead, so both came back null on
 * every run and nothing noticed: nothing on the page read them yet.
 */
function toConfig(run: NonNullable<MetadataPayload['run']>): RunConfigRef | null {
  const config = run.config;
  if (!config) return null;

  return {
    enginesHash: run.engines_hash ?? config.engines_hash ?? null,
    interviewAnalysisConfigId: config.id ?? config.interview_analysis_config_id ?? null,
    gesturesConfigId: config.gestures_mongo_config_id ?? null,
    version: config.version ?? null,
  };
}

function toStage(payload: StagePayload): RunStage {
  return {
    name: payload.name,
    status: payload.status,
    startedAt: date(payload.started_at),
    finishedAt: date(payload.finished_at),
    durationMs: payload.duration_ms ?? null,
  };
}

function toProvenance(payload: ProvenancePayload | null | undefined): AnalysisProvenance | null {
  if (!payload) return null;

  return {
    schemaVersion: payload.schema_version ?? null,
    analysisConfigId: payload.analysis_config_id ?? null,
    analysisConfigVersion: payload.analysis_config_version ?? null,
    analysisConfigHash: payload.analysis_config_hash ?? null,
    enginesHash: payload.engines_hash ?? null,
    degraded: typeof payload.degraded === 'boolean' ? payload.degraded : null,
    engine: toEngine(payload.engine),
  };
}

function toMetadata(payload: MetadataPayload): AnalysisMetadata {
  return {
    videoUrl: payload.video_url,
    video: {
      id: payload.video.id,
      name: payload.video.name,
      description: payload.video.description,
      createdAt: date(payload.video.created_at),
      durationSeconds: payload.video.duration_seconds,
      status: payload.video.status,
      processingSeconds: payload.video.processing_seconds ?? null,
      analysisStartedAt: date(payload.video.analysis_started_at),
      analysisCompletedAt: date(payload.video.analysis_completed_at),
    },
    analysisStatus: payload.analysis_status,
    analysisProgress: payload.analysis_progress,
    run: payload.run
      ? {
          runId: payload.run.run_id ?? null,
          status: payload.run.status ?? null,
          attempt: payload.run.attempt ?? 1,
          enginesRequested: payload.run.engines_requested ?? [],
          enginesExecuted: payload.run.engines_executed ?? [],
          enginesSkipped: payload.run.engines_skipped ?? [],
          engine: toEngine(payload.run.engine),
          config: toConfig(payload.run),
          isActive: payload.run.is_active === true,
          partial: payload.run.partial === true,
          batch: payload.run.batch ?? null,
          note: payload.run.note ?? null,
          startedAt: date(payload.run.started_at),
          finishedAt: date(payload.run.finished_at),
          stages: (payload.run.stages ?? []).map(toStage),
        }
      : null,
    provenance: toProvenance(payload.provenance),
    landmarksRetained: payload.landmarks_retained,
    // Two frame counts live in this block and they are not the same number: the video's and the
    // landmarks'. They differ by the sampling ratio — 393 against 131 on a real video — so reading
    // the video's would promise three times the frames there are to draw.
    landmarksConfig: payload.landmarks_config
      ? {
          totalFrames:
            payload.landmarks_config.landmarks_total_frames ??
            payload.landmarks_config.total_frames,
          fps: payload.landmarks_config.sampling_fps ?? payload.landmarks_config.fps,
        }
      : null,
  };
}

function toEpisode(payload: NonNullable<GesturesPayload['episodes']>[number]): GestureEpisode {
  return {
    episodeId: payload.episode_id,
    gestureName: payload.gesture_name,
    startMs: payload.start_time_ms,
    endMs: payload.end_time_ms,
    durationMs: payload.duration_ms,
    classification: payload.classification,
    shouldShow: payload.should_show !== false,
    analysisStatus: payload.analysis_status ?? null,
    analysisMethod: payload.analysis_method ?? null,
    discardReason: payload.discard_reason ?? null,
    frequencyBand: payload.frequency_band ?? null,
    absoluteCount: payload.absolute_count ?? null,
    confidence: toConfidence(payload.confidence_summary),
  };
}

/** A range, and it is only worth showing when the engine measured one. */
function toConfidence(
  summary: { mean?: number; min?: number; max?: number } | null | undefined,
): { mean: number; min: number; max: number } | null {
  if (!summary || typeof summary.mean !== 'number') return null;

  return {
    mean: summary.mean,
    min: summary.min ?? summary.mean,
    max: summary.max ?? summary.mean,
  };
}

/**
 * The engine's working, renamed and otherwise passed through.
 *
 * Deliberately tolerant: this bag grows a field every release, and a reader that insisted on a
 * shape would start dropping the new ones the day they appear.
 */
function toExplain(bag: Record<string, unknown> | null | undefined): GestureExplain | null {
  if (!bag) return null;

  const number = (key: string): number | undefined =>
    typeof bag[key] === 'number' ? (bag[key] as number) : undefined;
  const boolean = (key: string): boolean | undefined =>
    typeof bag[key] === 'boolean' ? (bag[key] as boolean) : undefined;
  const text = (key: string): string | null | undefined =>
    typeof bag[key] === 'string' ? (bag[key] as string) : undefined;

  return {
    confidence: number('confidence'),
    inBaseline: boolean('in_baseline'),
    overlapsSubjectSpeech: boolean('overlaps_subject_speech'),
    overlapsInterviewerSpeech: boolean('overlaps_interviewer_speech'),
    isSubjectSpeaking: boolean('is_subject_speaking'),
    analysisMethod: text('analysis_method') ?? undefined,
    frequencyBand: text('frequency_band') ?? undefined,
    absoluteCount: number('absolute_count'),
    localFreq: number('local_freq'),
    globalFreq: number('global_freq'),
    localEpisodeCount: number('local_episode_count'),
    suppressedReason: text('suppressed_reason') ?? null,
    notes: text('notes') ?? null,
    featureWeights: (bag.feature_weights as Record<string, number> | undefined) ?? undefined,
  };
}

function toAlert(payload: NonNullable<GesturesPayload['alerts']>[number]): GestureAlert {
  return {
    episodeId: payload.episode_id,
    gestureName: payload.gesture_name,
    timestampMs: payload.timestamp_ms,
    durationMs: payload.duration_ms,
    reason: payload.reason,
    gestureKind: payload.gesture_kind ?? null,
    isSubjectSpeaking: payload.is_subject_speaking ?? null,
    explain: toExplain(payload.explain),
  };
}

function toCoherenceAlert(payload: CoherenceAlertPayload): CoherenceAlert {
  return {
    alertId: payload.alert_id,
    episodeId: payload.source_alert_episode_id ?? null,
    gestureName: payload.gesture_name,
    timestampMs: payload.gesture_timestamp_ms,
    ruleId: payload.rule_id ?? null,
    ruleLabel: payload.rule_label ?? null,
    matched: payload.matched === true,
    reductionPct: payload.coherence_reduction ?? 0,
    // Kept apart from what applied: «-80 % (no aplicado)» needs both numbers, and collapsing
    // them into one loses the difference between a rule at zero and a rule that did not fire.
    potentialReductionPct: payload.rule_reduction_pct ?? 0,
    reasonCategory: payload.reason_category ?? null,
    confidence: payload.confidence ?? null,
    windowSeconds: payload.metadata?.window_seconds ?? null,
    reductionSource: payload.metadata?.reduction_source ?? null,
    coherenceBefore: finite(payload.coherence_before),
    coherenceAfter: finite(payload.coherence_after),
    explanation: payload.explanation?.trim() || null,
    semanticMatch: payload.semantic_match
      ? {
          text: payload.semantic_match.text ?? '',
          startMs: finite(payload.semantic_match.start_time_ms),
          endMs: finite(payload.semantic_match.end_time_ms),
          speaker: payload.semantic_match.speaker?.trim() || null,
        }
      : null,
    gestureRangeMs: toRange(payload.gesture_time_range_ms),
    semanticTimingAlignmentMs: finite(payload.semantic_timing_alignment_ms),
    sourceItem: payload.metadata?.source_item ?? null,
    sourceValue: payload.metadata?.source_value?.trim() || null,
    semanticCandidateSource: payload.metadata?.semantic_candidate_source ?? null,
    gestureKind: payload.metadata?.gesture_kind ?? null,
    isSubjectSpeaking: payload.metadata?.is_subject_speaking ?? null,
    segmentStrategy: payload.metadata?.segment_strategy ?? null,
    blockIds: {
      perAlert: payload.metadata?.semantic_block_ids_diarization_per_alert ?? [],
      merged: payload.metadata?.semantic_block_ids_diarization_merged ?? [],
      mergedParent: payload.metadata?.semantic_block_ids_diarization_merged_parent ?? [],
    },
    detectorExplain: toExplain(payload.explain),
  };
}

/** A number, or null — never a `NaN` that renders as «NaN %». */
function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function toRange(value: number[] | null | undefined): [number, number] | null {
  const start = finite(value?.[0]);
  const end = finite(value?.[1]);

  return start === null || end === null ? null : [start, end];
}

function toWord(payload: WordPayload): SemanticWord | null {
  const index = finite(payload.word_index);
  const startMs = finite(payload.start_time_ms);
  const endMs = finite(payload.end_time_ms);

  // A word without its place or its timing cannot be highlighted or sought to, so it is not one.
  if (index === null || startMs === null || endMs === null) return null;

  return {
    index,
    text: (payload.text ?? '').trim(),
    startMs,
    endMs,
    speaker: (payload.speaker ?? '').trim(),
    segmentIndex: finite(payload.source_segment_index),
  };
}

function flag(value: boolean | null | undefined): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function toBlock(payload: BlockPayload, position: number): SemanticBlock {
  return {
    blockId: payload.block_id ?? `block-${position}`,
    startMs: finite(payload.start_time_ms),
    endMs: finite(payload.end_time_ms),
    text: (payload.text ?? '').trim(),
    speaker: (payload.speaker ?? '').trim(),
    sourceSegmentIndex: finite(payload.source_segment_index),
    contributingAlertIds: (payload.contributing_alert_ids ?? []).map(String).filter(Boolean),
    highlightWordIndexes: (payload.highlight_word_indexes ?? []).filter(
      (index) => finite(index) !== null,
    ),
    anchorWordIndex: finite(payload.anchor_word_index),
    blockKind: payload.block_kind ?? '',
    parentBlockId: payload.parent_block_id ?? null,
    childBlockIds: payload.child_block_ids ?? [],
    scoreComputedSubtractionAfter: finite(payload.score_resta_computada_after),
    scoreAlertMajorAfter: finite(payload.score_alert_major_after),
    aggregateCoherenceAfter: finite(payload.aggregate_coherence_after),
    aggregateAlertCount: finite(payload.aggregate_alert_count),
    aggregateMatchedCount: finite(payload.aggregate_matched_count),
    belowThresholdSumReduction: flag(payload.below_threshold_sum_reduction),
    belowThresholdAlertMajor: flag(payload.below_threshold_alert_major),
    semanticAlertIds: (payload.semantic_alert_ids ?? []).map(String).filter(Boolean),
  };
}

const SEMANTIC_ORIGINS: readonly SemanticAlertOrigin[] = ['transcript', 'cv', 'dataset', 'context'];
const RULE_ORIGINS: readonly CoherenceRuleOrigin[] = ['gesture', ...SEMANTIC_ORIGINS];
const CONTRADICTION_KINDS: readonly ContradictionKind[] = [
  'idea',
  'cv',
  'video',
  'context',
  'none',
];
const RULE_STATUSES: readonly CoherenceRuleStatus[] = ['active', 'disabled', 'no_origin'];
const COMPARE_WITH: readonly CoherenceCompareWith[] = [
  'idea',
  'question',
  'overall',
  'transcript',
  'world',
  'reference',
];
const FINDING_STATUSES: readonly RuleFinding['status'][] = ['kept', 'discarded'];
const FINDING_REASONS: readonly RuleFindingReason[] = [
  'not_subject',
  'low_confidence',
  'duplicate',
  'no_reference',
  'no_other_idea',
  'unknown_idea',
];

function among<T extends string>(allowed: readonly T[], value: unknown): T | null {
  return allowed.includes(value as T) ? (value as T) : null;
}

/**
 * One semantic alert, or null for one the screen could not place or name.
 *
 * Without its id or its idea it hangs on nothing; with an origin this side does not know it would
 * be shown under a badge that names nothing. A contradiction of a kind it does not know is kept as
 * «none» with its text: the alert still subtracted, and the quote still says something.
 */
function toSemanticAlert(payload: SemanticAlertPayload): SemanticAlert | null {
  const origin = among(SEMANTIC_ORIGINS, payload.origin);

  if (!payload.alert_id || !payload.block_id || origin === null) return null;

  return {
    alertId: payload.alert_id,
    blockId: payload.block_id,
    ruleId: payload.rule_id ?? '',
    ruleLabel: (payload.rule_label ?? '').trim(),
    origin,
    text: (payload.text ?? '').trim(),
    startMs: finite(payload.start_time_ms),
    endMs: finite(payload.end_time_ms),
    speaker: (payload.speaker ?? '').trim(),
    question: (payload.question ?? '').trim(),
    contradicts: toContradiction(payload.contradicts),
    explanation: (payload.explanation ?? '').trim(),
    confidence: finite(payload.confidence),
    coherenceReduction: Math.max(0, finite(payload.coherence_reduction) ?? 0),
  };
}

function toContradiction(payload: SemanticAlertPayload['contradicts']): SemanticContradiction {
  const contradicts = payload ?? {};
  const sourceId = contradicts.source_id;

  return {
    kind: among(CONTRADICTION_KINDS, contradicts.kind) ?? 'none',
    text: (contradicts.text ?? '').trim(),
    blockId: contradicts.block_id ?? null,
    startMs: finite(contradicts.start_time_ms),
    endMs: finite(contradicts.end_time_ms),
    sourceId: sourceId === null || sourceId === undefined ? null : String(sourceId),
    sourceName: contradicts.source_name?.trim() || null,
  };
}

/**
 * One finding of the LLM (engine 7.3.0), or null for one that names no rule, no idea number or a
 * state this side has no name for: «descartado» and «se quedó» are claims, not defaults.
 */
/** Also read by the simulations, whose `rule_findings` have the same shape. */
export function toRuleFinding(payload: RuleFindingPayload): RuleFinding | null {
  const status = among(FINDING_STATUSES, payload.status);
  const ideaIndex = finite(payload.idea_index);

  if (!payload.rule_id || ideaIndex === null || status === null) return null;

  return {
    ruleId: payload.rule_id,
    ideaIndex,
    blockId: payload.block_id ?? null,
    startMs: finite(payload.start_time_ms),
    endMs: finite(payload.end_time_ms),
    text: (payload.text ?? '').trim(),
    contradicts: toContradiction(payload.contradicts),
    explanation: (payload.explanation ?? '').trim(),
    confidence: finite(payload.confidence),
    status,
    reason: among(FINDING_REASONS, payload.reason),
  };
}

function toSemanticAlerts(list: SemanticAlertPayload[] | null | undefined): SemanticAlert[] {
  return (list ?? [])
    .map(toSemanticAlert)
    .filter((alert): alert is SemanticAlert => alert !== null);
}

/**
 * `notes.coherence`, or null on a document from before the rules.
 *
 * A rule whose origin or state this side does not know is left out rather than shown as active:
 * «activa» is a claim that the run judged it, and nothing here says so.
 */
function toCoherenceNotes(payload: NotesPayload['coherence']): CoherenceRulesNotes | null {
  if (!payload) return null;

  return {
    thresholdPct: finite(payload.threshold_pct),
    personId: finite(payload.person_id),
    semanticAlerts: finite(payload.semantic_alerts),
    rules: (payload.rules ?? []).flatMap((rule) => {
      const origin = among(RULE_ORIGINS, rule.origin);
      const status = among(RULE_STATUSES, rule.status);

      if (!rule.rule_id || origin === null || status === null) return [];

      return [
        {
          ruleId: rule.rule_id,
          label: rule.label?.trim() || rule.rule_id,
          origin,
          reductionPct: finite(rule.reduction_pct) ?? 0,
          status,
          alerts: finite(rule.alerts) ?? 0,
          minConfidence: finite(rule.min_confidence),
          compareWith: among(COMPARE_WITH, rule.compare_with),
          calls: finite(rule.calls),
          findings: finite(rule.findings),
          discarded: finite(rule.discarded),
        },
      ];
    }),
    ruleFindings: Array.isArray(payload.rule_findings)
      ? payload.rule_findings
          .map(toRuleFinding)
          .filter((finding): finding is RuleFinding => finding !== null)
      : null,
  };
}

function toBlocks(list: BlockPayload[] | null | undefined): SemanticBlock[] {
  return (list ?? []).map(toBlock);
}

/**
 * The engine's ideas, or null when the document has no list of them.
 *
 * Null and empty are kept apart because they are different answers, and the legacy reads them so:
 * no list is «no result», an empty one is «cleared» — the engine scored the ideas and none fell.
 * An older document kept every strategy in one `blocks` list, and its ideas are the ones marked
 * as such, as the legacy reads them.
 */
function ideaBlocksOf(payload: DiarizationPayload): SemanticBlock[] | null {
  if (Array.isArray(payload.blocks_idea)) return toBlocks(payload.blocks_idea);
  if (Array.isArray(payload.blocks)) {
    return toBlocks(payload.blocks).filter((block) => block.blockKind === 'idea');
  }

  return null;
}

function toSemanticLayer(payload: DiarizationPayload | null | undefined): SemanticLayer | null {
  if (!payload) return null;

  const words = (payload.words ?? [])
    .map(toWord)
    .filter((word): word is SemanticWord => word !== null)
    .sort((a, b) => a.index - b.index || a.startMs - b.startMs);

  const speakerNames: SemanticLayer['speakerNames'] = {};
  for (const [label, entry] of Object.entries(payload.speaker_names ?? {})) {
    speakerNames[label] = {
      name: (entry?.name ?? '').trim(),
      confidence: finite(entry?.confidence),
    };
  }

  const merged = payload.blocks_merged ?? payload.blocks ?? [];

  return {
    words,
    speakerNames,
    blocksIdea: ideaBlocksOf(payload),
    blocksPerAlert: toBlocks(payload.blocks_per_alert),
    // Parents apart from children, as the engine now stores them. An older document mixed the two
    // in one list and marked the parents by kind, so that is separated here once.
    blocksMerged: toBlocks(merged).filter((block) => block.blockKind !== 'merged_parent'),
    blocksMergedParents:
      (payload.blocks_merged_parents ?? []).length > 0
        ? toBlocks(payload.blocks_merged_parents)
        : toBlocks(merged).filter((block) => block.blockKind === 'merged_parent'),
    blocksNone: Array.isArray(payload.blocks_none) ? toBlocks(payload.blocks_none) : null,
  };
}

function toNotes(payload: NotesPayload | null | undefined): CoherenceNotes | null {
  if (!payload) return null;

  const summary = payload.candidates_summary;
  const candidatesSummary: CandidatesSummary | null = summary
    ? {
        thresholdPct: finite(summary.threshold_pct),
        ideaBlocksTotal: finite(summary.idea_blocks_total),
        belowThresholdSumReductionTotal: finite(summary.below_threshold_sum_reduction_total),
        belowThresholdAlertMajorTotal: finite(summary.below_threshold_alert_major_total),
        candidates: (summary.candidates ?? []).map((candidate, position) => ({
          blockId: candidate.block_id ?? `candidate-${position}`,
          startMs: finite(candidate.start_time_ms),
          endMs: finite(candidate.end_time_ms),
          text: candidate.text ?? '',
          scoreComputedSubtractionAfter: finite(candidate.score_resta_computada_after),
          scoreAlertMajorAfter: finite(candidate.score_alert_major_after),
          gestures: (candidate.gestures ?? []).map((gesture) => ({
            gestureName: gesture.gesture_name ?? '',
            ruleId: gesture.rule_id ?? null,
            coherenceReduction: finite(gesture.coherence_reduction),
            frequencyBand: gesture.frequency_band ?? null,
            reason: gesture.reason ?? null,
          })),
        })),
      }
    : null;

  return {
    alertsTotal: finite(payload.alerts_total),
    coherenceRows: finite(payload.coherence_rows),
    alertsWithoutRuleBinding: finite(payload.alerts_without_rule_binding),
    duplicateSignalRows: finite(payload.duplicate_signal_rows),
    transcriptionSegments: finite(payload.transcription_segments),
    bulkBatches: finite(payload.bulk_batches),
    bulkItems: finite(payload.bulk_items),
    segmentStrategy: payload.segment_strategy ?? null,
    candidatesSummary,
    coherence: toCoherenceNotes(payload.coherence),
  };
}

function toCoherenceLayer(payload: GesturesPayload['coherence_analysis']): CoherenceLayer | null {
  if (!payload) return null;

  const modes = payload.semantic_modes;

  return {
    rulesetVersion: payload.ruleset_version ?? null,
    notes: toNotes(payload.notes),
    // Diarisation first, as the engine and the legacy both read it; punctuation is the older
    // mode and only stands in when a document has nothing else.
    diarization: toSemanticLayer(modes?.diarization ?? modes?.punctuation),
  };
}

/** The product defaults, used only when the run sealed none of its own. The panel says so. */
const DEFAULT_THRESHOLDS = { good: 80, warn: 50 } as const;
const DEFAULT_IDEA_THRESHOLD_PCT = 50;

/**
 * What a sealed rule may lack.
 *
 * A snapshot is whatever the ruleset looked like when the run sealed it, and older ones carry fewer
 * keys than the catalogue does today. A missing label must read as empty, not throw in the dialog.
 */
const SNAPSHOT_RULE_DEFAULTS: Omit<RulePayload, 'ruleId'> = {
  ruleLabel: '',
  sourceItem: '',
  sourceValue: '',
  reduction: 0,
  windowSeconds: 0,
  semanticIntents: [],
  requiresIsSpeaking: null,
};

function numberIn(value: unknown, min: number, max: number): number | null {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;

  return typeof number === 'number' && Number.isFinite(number) && number >= min && number <= max
    ? number
    : null;
}

function toCoherenceConfig(
  payload: GesturesPayload['analysis_config_snapshot'],
): CoherenceConfig | null {
  if (!payload) return null;

  const coherence = payload.engines?.coherence ?? {};
  const good = numberIn(coherence.semaphoreThresholdGood, 0, 100);
  const warn = numberIn(coherence.semaphoreThresholdWarn, 0, 100);
  // Both or neither: one sealed threshold beside one default would colour by a pair nobody chose.
  const sealed = good !== null && warn !== null;
  // The legacy's order and its range guard: an out-of-range value is a broken snapshot, not 150 %.
  const ideaThreshold =
    numberIn(coherence.ideaCoincidenceThresholdPct, 0, 100) ??
    numberIn(coherence.detectionThresholdPct, 0, 100);

  return {
    thresholds: sealed ? { good, warn } : { ...DEFAULT_THRESHOLDS },
    thresholdsDefaulted: !sealed,
    ideaThresholdPct: ideaThreshold ?? DEFAULT_IDEA_THRESHOLD_PCT,
    ideaThresholdDefaulted: ideaThreshold === null,
    segmentStrategy: coherence.segmentStrategy ?? null,
    semanticWindowSeconds: finite(coherence.semanticWindowSeconds),
    transcriptionProvider: coherence.transcriptionProvider ?? null,
    rulesetVersion: coherence.ruleset?.version ?? null,
    rules: (coherence.ruleset?.rules ?? [])
      .filter((rule) => Boolean(rule?.ruleId))
      .map((rule) => toRule({ ...SNAPSHOT_RULE_DEFAULTS, ...rule })),
  };
}

/** Null when the document names none of the three lists: a document from before they existed. */
function toEnginesRun(payload: GesturesPayload): EnginesRun | null {
  const requested = payload.engines_requested;
  const executed = payload.engines_executed;
  const skipped = payload.engines_skipped;

  if (!Array.isArray(requested) && !Array.isArray(executed) && !Array.isArray(skipped)) {
    return null;
  }

  return {
    requested: requested ?? [],
    executed: executed ?? [],
    skipped: (skipped ?? [])
      .map((item) => ({ name: String(item?.name ?? ''), reason: String(item?.reason ?? '') }))
      .filter((item) => item.name !== ''),
    partial: payload.partial === true,
  };
}

/**
 * One gestures document, translated.
 *
 * Exported so the semantic panel's spec can run the real 305 payload through the same reading the
 * screen gets, rather than a hand-built copy that would agree with whatever this file believes.
 */
export function toGestureAnalysis(payload: GesturesPayload): GestureAnalysis {
  const coherenceAnalysis = payload.coherence_analysis;
  // Present only when it carries the alert list: that is what says the engine actually wrote it.
  const hasCoherence = Array.isArray(coherenceAnalysis?.coherence_alerts);

  return {
    episodes: (payload.episodes ?? []).map(toEpisode),
    alerts: (payload.alerts ?? []).map(toAlert),
    coherenceAlerts: (
      payload.coherence_analysis?.coherence_alerts ??
      payload.coherence_alerts ??
      []
    ).map(toCoherenceAlert),
    semanticAlerts: toSemanticAlerts(coherenceAnalysis?.semantic_alerts),
    baselineByGesture: payload.baseline?.per_gesture_frequency_hz ?? {},
    coherence: hasCoherence ? toCoherenceLayer(coherenceAnalysis) : null,
    coherenceConfig: toCoherenceConfig(payload.analysis_config_snapshot),
    provenance:
      'schema_version' in payload || 'analysis_config_id' in payload ? toProvenance(payload) : null,
    enginesRun: toEnginesRun(payload),
  };
}

/**
 * Boundary with one analysis of one video.
 *
 * Four reads, kept separate because the pieces weigh wildly different amounts. Every one takes an
 * optional run id: absent is the active analysis, and naming one is what lets an old run be
 * reviewed at all.
 */
export const analysisDetailApi = {
  metadata: async (videoId: number, runId?: string): Promise<AnalysisMetadata> =>
    toMetadata(
      await httpClient.get<MetadataPayload>(
        `/virtual-profiler/videos/${videoId}/analysis/metadata${runId ? `?run=${encodeURIComponent(runId)}` : ''}`,
      ),
    ),

  gestures: async (videoId: number, runId?: string): Promise<GestureAnalysis> => {
    const payload = await httpClient.get<GesturesPayload>(
      `/virtual-profiler/videos/${videoId}/analysis/gestures${runId ? `?run=${encodeURIComponent(runId)}` : ''}`,
    );

    return toGestureAnalysis(payload);
  },

  transcription: async (videoId: number, runId?: string): Promise<Transcription> => {
    const payload = await httpClient.get<TranscriptionPayload>(
      `/virtual-profiler/videos/${videoId}/analysis/transcription${runId ? `?run=${encodeURIComponent(runId)}` : ''}`,
    );

    return {
      segments: (payload.transcription ?? payload.segments ?? []).map((segment) => ({
        startMs: toMs(segment.startTime),
        endMs: toMs(segment.endTime),
        // Upstream's own word for a speaker it could not tell. `candidate` was an invention, and
        // it put the interviewer's question in the candidate's mouth.
        speaker: segment.speaker ?? 'unknown',
        text: segment.text,
      })),
      // Words carry no speaker: coherence assigns one later, by the segment they fall in.
      words: (payload.words ?? []).map((word) => ({
        word: word.word,
        startMs: toMs(word.start_time),
        endMs: toMs(word.end_time),
        speaker: 'unknown',
      })),
    };
  },

  landmarks: async (
    videoId: number,
    window: { startSeconds: number; endSeconds: number },
    runId?: string,
  ): Promise<LandmarkWindow> => {
    const query = new URLSearchParams({
      startSeconds: String(window.startSeconds),
      endSeconds: String(window.endSeconds),
    });
    if (runId) query.set('run', runId);

    const payload = await httpClient.get<LandmarksPayload>(
      `/virtual-profiler/videos/${videoId}/analysis/landmarks?${query}`,
    );

    const frames: LandmarkFrame[] = (payload.frames ?? []).map((frame) => ({
      timestampMs: frame.timestamp_ms ?? 0,
      face: frame.face ?? [],
      leftHand: frame.left_hand ?? [],
      rightHand: frame.right_hand ?? [],
    }));

    return { frames, startSeconds: payload.start_seconds, endSeconds: payload.end_seconds };
  },
};
